import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { partnerCommitments, partnerFaq } from '@/mocks/homeSections';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import FaqAccordion from '@/components/feature/FaqAccordion';

export default function HonestyStatement() {
  const statementAnim = useScrollAnimation({ threshold: 0.1 });
  const faqAnim = useScrollAnimation({ threshold: 0.1 });
  const { hash } = useLocation();

  useEffect(() => {
    if (hash !== '#our-honest-position') return undefined;
    const timer = window.setTimeout(() => {
      document.getElementById('our-honest-position')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [hash]);

  return (
    <section id="our-honest-position" className="w-full py-14 md:py-20 bg-white scroll-mt-20">
      <div className="w-full px-4 md:px-6 lg:px-8">
        {/* Main statement */}
        <div
          ref={statementAnim.ref}
          className={`max-w-6xl mx-auto animate-on-scroll ${statementAnim.isVisible ? 'animate-visible' : ''}`}
        >
          <div className="max-w-3xl mb-10 md:mb-12">
            <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
              Our Honest Position
            </p>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900 leading-tight">
              A Partner, <span className="text-gold-600">Not a Promise</span>
            </h2>
            <div className="w-16 h-1 bg-gold-400 rounded-full mt-5 mb-6"></div>
            <p className="text-sm md:text-base text-navy-700 leading-relaxed">
              There is a version of this industry that sells certainty. It promises bestseller lists before
              the manuscript is finished, guaranteed sales before a single reader has opened the book, and a
              perfect record of reviews that no real company could earn. That is not us, and we would rather
              lose your business to honesty than win it with something we cannot deliver.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Long form */}
            <div className="lg:col-span-7 space-y-4 text-sm md:text-[15px] text-navy-700 leading-relaxed">
              <h3 className="text-lg font-semibold text-navy-900">
                We represent your work. We do not claim it.
              </h3>
              <p>
                Leavitt Peak Press is an independent press that represents authors. That word matters to us.
                Representation means we carry your title into rooms you may never enter &mdash; production
                houses, retail catalogues, distributors, and the exhibition halls of international book fairs
                &mdash; and we speak for it there. Your name stays on the cover. Your voice stays in the
                sentences. We do not take credit for either.
              </p>

              <h3 className="text-lg font-semibold text-navy-900 pt-2">
                What we control, and what we do not
              </h3>
              <p>
                We can control the quality of production, the accuracy of a schedule, the number of
                catalogues your book appears in, and whether we are physically standing at a fair with your
                title on a table. We cannot control how many readers buy it, how retailers rank it, what
                reviewers decide, or what the market does six months from now. Any company that claims
                otherwise is describing luck as a service.
              </p>
              <p>
                So we make a different kind of commitment: we tell you what we will do in writing, we do
                that work, and we show you evidence of it &mdash; booth numbers, fair catalogues,
                photographs, and retail listings. Visibility is the deliverable. Outcome is yours, and no
                honest publisher should pretend to own it.
              </p>

              <h3 className="text-lg font-semibold text-navy-900 pt-2">
                On feedback, and where it comes from
              </h3>
              <p>
                Every testimonial on this site belongs to an author who actually worked with us &mdash;
                someone who sat through the production meetings, saw the booth, and lived the process from
                manuscript to shelf. We do not write them, we do not buy them, and we do not publish
                second-hand remarks from people who never signed with us.
              </p>
              <p>
                If you search hard, you may notice that you will not find a long trail of complaints written
                by our authors, and you may also notice that some of the loudest negative voices online are
                not authors at all. We do not spend our energy arguing with competitors who file reports
                about us. We would rather answer to the people who know our work first-hand. Authentic
                experience, in the author&rsquo;s own words, is the only kind of feedback this press will put
                its name behind.
              </p>
              <p className="text-navy-900 font-medium">
                If that sounds like a partner you can rely on, we would like to hear about your book. If what
                you actually want is a guarantee, we are not the right press &mdash; and we will tell you so
                on the first call.
              </p>
            </div>

            {/* Commitments card */}
            <div className="lg:col-span-5 w-full">
              <div className="bg-cream-50 border border-cream-200 rounded-2xl p-6 md:p-7">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold-100 text-gold-600 shrink-0">
                    <i className="ri-shield-check-line text-lg"></i>
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-navy-900">What you can hold us to</h3>
                    <p className="text-xs text-navy-500 mt-0.5">Four commitments, no fine print</p>
                  </div>
                </div>

                <ul className="space-y-5">
                  {partnerCommitments.map((item) => (
                    <li key={item.title} className="flex items-start gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-cream-200 text-gold-600 shrink-0 mt-0.5">
                        <i className={`${item.icon} text-sm`}></i>
                      </span>
                      <div className="min-w-0">
                        <h4 className="text-sm font-semibold text-navy-900 mb-1">{item.title}</h4>
                        <p className="text-xs md:text-sm text-navy-600 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-5 border-t border-cream-200 flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-gold-500 text-white shrink-0">
                    <i className="ri-chat-3-line text-sm"></i>
                  </div>
                  <p className="text-xs text-navy-600 leading-relaxed">
                    Want to speak with an author we have worked with before you decide? Ask us &mdash; where
                    that author is willing, we will make the introduction.
                  </p>
                </div>

                <Link
                  to="/events/verified-participation"
                  className="mt-4 flex items-center justify-between gap-3 px-4 py-3 bg-white border border-cream-200 rounded-xl hover:border-gold-300 transition-colors duration-300 group"
                >
                  <span className="text-xs font-semibold text-navy-900 leading-snug">
                    Check the booth numbers and fair photographs yourself
                  </span>
                  <span className="w-7 h-7 flex items-center justify-center rounded-full bg-gold-50 text-gold-600 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* A word about critics */}
        <div
          className={`max-w-6xl mx-auto mt-10 md:mt-12 animate-on-scroll ${statementAnim.isVisible ? 'animate-visible' : ''}`}
        >
          <div className="border-l-4 border-gold-400 bg-cream-50 rounded-r-xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-white border border-cream-200 text-gold-600 shrink-0">
                <i className="ri-hand-heart-line text-lg"></i>
              </span>
              <div className="min-w-0">
                <h3 className="text-base md:text-lg font-semibold text-navy-900 mb-3">
                  A word about the organizations that warn against us
                </h3>
                <div className="space-y-3 text-sm md:text-[15px] text-navy-700 leading-relaxed">
                  <p>
                    We are aware that some organizations have placed Leavitt Peak Press in a less
                    flattering light. We do not resent it. We understand and respect their position,
                    because they are doing what they believe protects authors &mdash; the very same thing we
                    do for every first-time and returning author who chooses to work with us.
                  </p>
                  <p>
                    We have been in this business for almost five years. In that time, if you go looking,
                    you will not find a body of reviews on Facebook, Google, or any other platform written
                    by real authors describing what we do and what we promise. What you are far more likely
                    to find are articles from entities who have no direct relationship with our work and no
                    experience of it. We have chosen not to spend our years arguing with that.
                  </p>
                  <p className="text-navy-900 font-medium">
                    We will keep doing what is right and what is best for our authors &mdash; with the
                    integrity that we, and the authors we work with every day, already know.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div
          ref={faqAnim.ref}
          className={`max-w-6xl mx-auto mt-12 md:mt-16 animate-on-scroll ${faqAnim.isVisible ? 'animate-visible' : ''}`}
        >
          <div className="bg-cream-50 border border-cream-200 rounded-2xl p-6 md:p-9">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-2">
              <div>
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-2">
                  Straight Answers
                </p>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900">
                  The questions authors actually ask us
                </h3>
              </div>
              <p className="text-xs text-navy-500 md:text-right max-w-[240px]">
                Including the uncomfortable ones. Nothing here is a promise.
              </p>
            </div>
            <div className="mt-5">
              <FaqAccordion items={partnerFaq} variant="light" defaultOpen={0} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}